export const SALARY_FREQUENCY_OPTIONS = [
  { value: 1, label: "Annually" },
  { value: 2, label: "Monthly" },
  { value: 3, label: "Weekly" },
  { value: 4, label: "Daily" },
  { value: 5, label: "Hourly" },
];

/**
 * Normalise whatever the API gives us for the salary period into the numeric
 * option value the Select expects.
 *
 * The options above are keyed by numeric pk, but `salary_period` comes back from
 * GET job-details in whichever form the serializer chose — the display label
 * ("Annually"), a numeric string ("1"), or the pk (1). Only the pk matches an
 * option, so anything else left the mandatory "Salary Frequency" dropdown blank
 * when editing a posted job even though a value was saved.
 *
 * Returns undefined for anything unrecognised, so the field stays empty (and its
 * required rule fires) rather than being set to a value no option can render.
 */
export const resolveSalaryFrequency = (raw) => {
  if (raw === null || raw === undefined || raw === "") return undefined;

  // pk, or a numeric string of one.
  if (/^\d+$/.test(String(raw).trim())) {
    const asNumber = Number(String(raw).trim());
    return SALARY_FREQUENCY_OPTIONS.some((o) => o.value === asNumber)
      ? asNumber
      : undefined;
  }

  // Display label, case/whitespace-insensitive.
  const label = String(raw).trim().toLowerCase();
  return SALARY_FREQUENCY_OPTIONS.find((o) => o.label.toLowerCase() === label)
    ?.value;
};

/**
 * Display label for a salary period — "Annually", "Hourly", … — from any of the
 * raw shapes resolveSalaryFrequency accepts. "" when unset/unrecognised.
 */
export const getSalaryFrequencyLabel = (raw) => {
  const value = resolveSalaryFrequency(raw);
  return SALARY_FREQUENCY_OPTIONS.find((o) => o.value === value)?.label ?? "";
};

/** pk → the noun that reads correctly after an amount ("120 - 150 per year"). */
const SALARY_FREQUENCY_UNITS = {
  1: "year",
  2: "month",
  3: "week",
  4: "day",
  5: "hour",
};

/**
 * Period suffix for a displayed salary range — "per year", "per hour", …
 *
 * Takes the same raw shapes as resolveSalaryFrequency (pk, numeric string or
 * display label) and returns "" when the period is missing or unrecognised, so
 * callers can drop it straight into JSX without guarding.
 *
 * The option labels are adverbs ("Annually"), which don't read after "per", so
 * the pk is mapped to a lowercase unit noun instead.
 */
export const getSalaryPeriodSuffix = (raw) => {
  const unit = SALARY_FREQUENCY_UNITS[resolveSalaryFrequency(raw)];
  return unit ? `per ${unit}` : "";
};

export const employmentType = {
  1: "full-time",
  2: "part-time",
  3: "remote",
  4: "temporary",
  5: "contract",
  6: "freelancer",
  7: "internship",
};
export const yearsOfExperience = {
  1: "0-1",
  2: "1-2",
  3: "2-3",
  4: "3-4",
  5: "4-5",
  6: "5+",
};

export const JOBS_LIST_PAGE_SIZE = 12;
export const INVITED_APPLICANTS_PAGE_SIZE = 12;

export const seniority_level = {
  1: "entry-level",
  2: "mid-level",
  3: "senior-level",
  4: "director-level",
  5: "executve",
};

export const qualifications = {
  1: "unspecified",
  2: "high school or equivalent",
  3: "diploma",
  4: "bachelors degree",
  5: "masters degree",
  6: "doctorate",
};

export const mode_of_work = {
  1: "On-site",
  2: "Remote",
  3: "Hybrid",
};

export const no_of_employees1 = {
  1: "0-50",
  2: "50-100",
  3: "101-200",
  4: "201-300",
  5: "301-500",
  6: "501-1000+",
};

export const getEmploymentType = (key) => {
  return employmentType[key] || "Unknown";
};

export const getExperience = (key) => {
  return yearsOfExperience[key] || "Unknown";
};

export const getSeniority = (jobDetails, key) => {
  return seniority_level[key] || "Unknown";
};

export const getModeOfWork = (jobDetails, key) => {
  return mode_of_work[key] || "Unknown";
};

export const getQualification = (jobDetails, key) => {
  if (
    jobDetails &&
    jobDetails.qualifications &&
    typeof jobDetails.qualifications[key] !== "undefined"
  ) {
    return jobDetails.qualifications[key];
  }
  return "Unknown";
};

export const zeroCreditError =
  "You don't have enough credits to Post the Job. Please Add Credits to your account.";

export const freeFeatures = [
  "Conduct AI-guided interviews.",
  "Obtain evaluations for candidates.",
  "Generate interview transcripts.",
  "Access candidate profiles and resumes.",
  "List jobs or allocate interviews to one job.",
];

export const businessFeatures = [
  "Allocate credits to jobs as needed.",
  "Post jobs with AI assistance.",
  "Interview candidates with AI.",
  "Receive AI - generated candidate assessments.",
  "Obtain automated transcripts of interviews.",
  "Access candidate profiles and resumes.",
];

export const enterpriseFeatures = [
  "Allocate credits to jobs as needed.",
  "Post jobs with AI assistance.",
  "Interview candidates with AI.",
  "Receive AI-generated candidate assessments.",
  "Obtain automated transcripts of interviews.",
  "Access candidate profiles and resumes.",
  "Bigger discounts on larger credit purchases.",
];

export const jobStatus = [
  { id: 1, status: "Draft" },
  { id: 2, status: "Published" },
  { id: 3, status: "Closed" },
  { id: 4, status: "Inactive" },
];

/**
 * Job status as it should be shown to a user.
 *
 * Accepts either the pk or the name the API sends (`"Inactive"`), because
 * different endpoints send different shapes. "Published" is the backend's word
 * for the live state — users see "Active" — so that one is relabelled.
 *
 * Returns "" for missing/placeholder values so callers can skip the badge.
 */
export const getJobStatusLabel = (raw) => {
  if (raw === null || raw === undefined || raw === "" || raw === "—") return "";

  const name = /^\d+$/.test(String(raw).trim())
    ? jobStatus.find((s) => s.id === Number(String(raw).trim()))?.status
    : String(raw).trim();
  if (!name) return "";

  const titleCased =
    name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
  return titleCased === "Published" ? "Active" : titleCased;
};

/**
 * Badge colours for a status label from getJobStatusLabel. Unknown values fall
 * back to the neutral grey rather than going unstyled.
 */
export const getJobStatusBadgeStyle = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "active") return { bg: "#D9F8E4", color: "#15803D" };
  if (s === "inactive") return { bg: "#FFE4E4", color: "#DC2626" };
  if (s === "closed") return { bg: "#FFE4E4", color: "#DC2626" };
  if (s === "draft") return { bg: "#F3F4F6", color: "#6B7280" };
  return { bg: "#F3F4F6", color: "#6B7280" };
};

export const ResumeSuggestions = [
  "Tools",
  "Certification",
  "Awards & Achievements",
  "Hobbies",
  "Project",
  "Languages",
  "Publications",
];

export const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/i;

export const S3_PUBLIC_BASE_URL =
  "https://aws-haire-bucket.s3.us-east-1.amazonaws.com/backup";

export const SPEAKER_TEST_AUDIO_URL = `${S3_PUBLIC_BASE_URL}/development/test.wav`;

export const LANGUAGE_VERSIONS = {
  javascript: "20.11.1",
  python: "3.12.0",
  java: "15.0.2",
  csharp: "6.12.0",
  php: "8.2.3",
};

export const quillModules = {
  toolbar: [["bold", "italic"], [{ list: "bullet" }]],
};

export const quillFormats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

export const questionPoints = {
  MCQ: 1,
  CODING: 5,
  SHORT: 5,
  ESSAY: 5,
};

// Application Status Constants
export const APPLICATION_STATUS = {
  SHORTLISTED: 1,
  REJECTED: 2,
  UNDER_REVIEW: 3,
  FIT_FOR_JOB: 4,
  NOT_FIT: 5,
  // REQUESTED: 6,
  MISSED: 7,
  ASSESSMENT_FAILED: 8,
  ASSESSMENT_PASSED: 9,
  // FOLLOW_UP: 10,
  HIRED: 11,
};

export const APPLICATION_STATUS_LABELS = {
  [APPLICATION_STATUS.SHORTLISTED]: "Shortlisted",
  [APPLICATION_STATUS.REJECTED]: "Rejected",
  [APPLICATION_STATUS.UNDER_REVIEW]: "Under-Review",
  [APPLICATION_STATUS.FIT_FOR_JOB]: "Fit-For-Job",
  [APPLICATION_STATUS.NOT_FIT]: "Not-Fit",
  // [APPLICATION_STATUS.REQUESTED]: "Requested",
  [APPLICATION_STATUS.MISSED]: "Missed",
  [APPLICATION_STATUS.ASSESSMENT_FAILED]: "Assessment Failed",
  [APPLICATION_STATUS.ASSESSMENT_PASSED]: "Assessment Passed",
  // [APPLICATION_STATUS.FOLLOW_UP]: "Follow-up",
  [APPLICATION_STATUS.HIRED]: "Hired",
};

// Display labels mapping (for UI display - different from backend labels)
// Note: "Interested" status (from backend) displays as "Applied" in UI
// Note: "Fit-For-Job" status (ID 4) displays as "Qualified" in UI
export const APPLICATION_STATUS_DISPLAY_LABELS = {
  [APPLICATION_STATUS.SHORTLISTED]: "Shortlisted",
  [APPLICATION_STATUS.REJECTED]: "Rejected",
  [APPLICATION_STATUS.UNDER_REVIEW]: "Under-Review",
  [APPLICATION_STATUS.FIT_FOR_JOB]: "Qualified", // Display "Qualified" instead of "Fit-For-Job"
  [APPLICATION_STATUS.NOT_FIT]: "Not-Applicable",
  // [APPLICATION_STATUS.REQUESTED]: "Requested",
  [APPLICATION_STATUS.MISSED]: "Missed",
  [APPLICATION_STATUS.ASSESSMENT_FAILED]: "Assessment Failed",
  [APPLICATION_STATUS.ASSESSMENT_PASSED]: "Assessment Passed",
  // [APPLICATION_STATUS.FOLLOW_UP]: "Follow-up",
  [APPLICATION_STATUS.HIRED]: "Hired",
};

export const getApplicationStatusLabel = (statusId) => {
  return APPLICATION_STATUS_LABELS[statusId] || "Unknown";
};

// Get display label (for UI) - shows "Applied" for Interested and "Qualified" for Fit-For-Job
export const getApplicationStatusDisplayLabel = (statusId) => {
  return (
    APPLICATION_STATUS_DISPLAY_LABELS[statusId] ||
    APPLICATION_STATUS_LABELS[statusId] ||
    "Unknown"
  );
};

export const getApplicationStatusId = (statusLabel) => {
  const entry = Object.entries(APPLICATION_STATUS_LABELS).find(
    ([id, label]) => label === statusLabel,
  );
  return entry ? parseInt(entry[0]) : null;
};

// Get display label from status string (for UI display)
// Handles "Interested" → "Applied" and "Fit-For-Job" → "Qualified"
// Also handles Assessment Failed/Passed → "Qualified"
// Lead Candidate Status IDs (for lead candidates only - Rejected and Follow_Up)
// These are specific IDs for lead candidate status updates
export const LEAD_CANDIDATE_STATUS = {
  REJECTED: 1, // Rejected status ID for lead candidates
  // FOLLOW_UP: 2, // Follow_Up status ID for lead candidates
  INTERESTED: 3, // Interested status ID for lead candidates
};

// Lead Candidate Status Labels mapping (reverse lookup: label -> ID)
export const LEAD_CANDIDATE_STATUS_MAP = {
  Rejected: LEAD_CANDIDATE_STATUS.REJECTED,
  // "Follow-up": LEAD_CANDIDATE_STATUS.FOLLOW_UP,
  // Follow_Up: LEAD_CANDIDATE_STATUS.FOLLOW_UP, // Support both formats
  Interested: LEAD_CANDIDATE_STATUS.INTERESTED,
};

// Get lead candidate status ID from status label
export const getLeadCandidateStatusId = (statusLabel) => {
  const statusId = LEAD_CANDIDATE_STATUS_MAP[statusLabel];
  // Return null if ID is not configured yet (will be replaced when IDs are provided)
  return statusId !== undefined ? statusId : null;
};

export const getApplicationStatusDisplayLabelFromString = (statusString) => {
  if (!statusString) return "Unknown";

  // Handle "Interested" status from backend - display as "Applied"
  if (statusString === "Interested") {
    return "Applied";
  }

  // Handle "Fit-For-Job" status - display as "Qualified"
  if (statusString === "Fit-For-Job") {
    return "Qualified";
  }

  // Handle Assessment Failed/Passed - display as "Qualified"
  if (
    statusString === "Assessment Failed" ||
    statusString === "Assessment Passed"
  ) {
    return "Qualified";
  }

  if (statusString === "Not-Fit") {
    return "Not-Applicable";
  }

  // For all other statuses, return as-is
  return statusString;
};

// Application Stage Constants

export const APPLICATION_STAGE = {
  PRE_SCREENED: 0,
  RESUME_SCREENED: 1,
  PHONE_SCREEN: 2,
  WRITTEN_ASSIGNMENT: 3,
  AI_INTERVIEW: 4,
  PANEL_INTERVIEW: 5,
  BACKGROUND_CHECK: 6,
  OFFER_EXTENDED: 7,
};

export const APPLICATION_STAGE_LABELS = {
  [APPLICATION_STAGE.PRE_SCREENED]: "Pre-Screened",
  [APPLICATION_STAGE.RESUME_SCREENED]: "Resume Screened",
  [APPLICATION_STAGE.PHONE_SCREEN]: "Phone Screening",
  [APPLICATION_STAGE.WRITTEN_ASSIGNMENT]: "Written Assessment",
  [APPLICATION_STAGE.AI_INTERVIEW]: "AI Interview",
  [APPLICATION_STAGE.PANEL_INTERVIEW]: "Panel Interview",
  [APPLICATION_STAGE.BACKGROUND_CHECK]: "Background Check",
  [APPLICATION_STAGE.OFFER_EXTENDED]: "Offer Extended",
};

export const getApplicationStageLabel = (stageId) => {
  return APPLICATION_STAGE_LABELS[stageId] || "Unknown";
};

export const getApplicationStageId = (stageLabel) => {
  const entry = Object.entries(APPLICATION_STAGE_LABELS).find(
    ([id, label]) => label === stageLabel,
  );
  return entry ? parseInt(entry[0]) : null;
};
